import { createSelector } from 'reselect';
import {
  getSpouse,
  getSpouseAddress,
  getSpouseAddressMatchWithApplicant,
  getSpouseBirthDate,
  getSpouseCitizenship,
  getSpouseDocumentDivisionCode,
  getSpouseDocumentIssueDate,
  getSpouseDocumentIssuer,
  getSpouseDocumentNumber,
  getSpouseDocumentType,
  getSpouseName,
  getSpousePatronimic,
  getSpouseSurname,
  isSpouseAddressMatchWithApplicant,
  isSpouseDocumentIsRussianPassport,
  isSpouseRegisteredInMoscow,
} from '../selectors/spouse';
import {
  isNameValid,
  notEmptyField,
  validateDocumentDivisionCode,
  validateDocumentIssueDate,
  validateDocumentNumber,
} from './common';
import { isDateInAcceptableRange } from '../helpers/dateHelpers';
import {
  validateAddressAddress,
  validateAddressArea,
  validateAddressDistrict,
  validateAddressFlat,
  validateAddressHouseAndBuilding,
  validateAddressOMK,
  validateAddressStreet,
} from '../selectors/address';
import { getApplicantMarriedOrLivingTogether } from '../selectors/applicant';
import {
  validatePersonBirthPlace,
  validatePersonChangedName,
  validatePersonChangedNameDocIssuer,
  validatePersonChangedPatronimic,
  validatePersonChangedSurname,
  validatePersonChangeName,
} from '../selectors/person';
import { validateDateOfMarriageWithSpousesDates } from './dates';

export const validateSpouseName = createSelector(
  getApplicantMarriedOrLivingTogether,
  getSpouseName,
  (hasSpouse, value) => hasSpouse && isNameValid(value)
);

export const validateSpouseSurname = createSelector(
  getApplicantMarriedOrLivingTogether,
  getSpouseSurname,
  (hasSpouse, value) => hasSpouse && isNameValid(value)
);
export const validateSpousePatronimic = createSelector(
  getApplicantMarriedOrLivingTogether,
  getSpousePatronimic,
  (hasSpouse, value) => hasSpouse && isNameValid(value)
);

export const validateSpouseCitizenship = createSelector(
  getApplicantMarriedOrLivingTogether,
  getSpouseCitizenship,
  (hasSpouse, value) => hasSpouse && !value
);

export const validateSpouseDocumentType = createSelector(
  getApplicantMarriedOrLivingTogether,
  getSpouseDocumentType,
  (hasSpouse, value) => hasSpouse && notEmptyField(value)
);

export const validateSpouseDocumentIssuer = createSelector(
  getApplicantMarriedOrLivingTogether,
  getSpouseDocumentIssuer,
  (hasSpouse, value) => hasSpouse && notEmptyField(value)
);

export const validateSpouseDocumentDivisionCode = createSelector(
  getApplicantMarriedOrLivingTogether,
  getSpouseDocumentDivisionCode,
  isSpouseDocumentIsRussianPassport,
  (hasSpouse, code, isRussianPassport) =>
    hasSpouse && validateDocumentDivisionCode(isRussianPassport, code)
);

export const validateSpouseDocumentIssuedDate = createSelector(
  getApplicantMarriedOrLivingTogether,
  getSpouseDocumentIssueDate,
  getSpouseBirthDate,
  isSpouseDocumentIsRussianPassport,
  (hasSpouse, date, birthDate, isRussianPassport) =>
    hasSpouse && validateDocumentIssueDate(date, birthDate, isRussianPassport)
);

export const validateSpouseDocumentNumber = createSelector(
  getApplicantMarriedOrLivingTogether,
  getSpouseDocumentNumber,
  isSpouseDocumentIsRussianPassport,
  (hasSpouse, number, isRussianPassport) =>
    hasSpouse && validateDocumentNumber(isRussianPassport, number)
);
export const validateSpouseBirthPlace = createSelector(
  getApplicantMarriedOrLivingTogether,
  getSpouse,
  (hasSpouse, person) => hasSpouse && validatePersonBirthPlace({ person })
);

export const validateSpouseAddressMatchWithApplicant = createSelector(
  getApplicantMarriedOrLivingTogether,
  getSpouseAddressMatchWithApplicant,
  (hasSpouse, value) => hasSpouse && notEmptyField(value)
);

export const validateSpouseChangeName = createSelector(
  getApplicantMarriedOrLivingTogether,
  getSpouse,
  (hasSpouse, person) => hasSpouse && validatePersonChangeName({ person })
);

export const validateSpouseChangedName = createSelector(
  getApplicantMarriedOrLivingTogether,
  getSpouse,
  (hasSpouse, person) => hasSpouse && validatePersonChangedName({ person })
);

export const validateSpouseChangedSurname = createSelector(
  getApplicantMarriedOrLivingTogether,
  getSpouse,
  (hasSpouse, person) => hasSpouse && validatePersonChangedSurname({ person })
);

export const validateSpouseChangedPatronimic = createSelector(
  getApplicantMarriedOrLivingTogether,
  getSpouse,
  (hasSpouse, person) =>
    hasSpouse && validatePersonChangedPatronimic({ person })
);

export const validateSpouseChangeNameDocIssuer = createSelector(
  getApplicantMarriedOrLivingTogether,
  getSpouse,
  (hasSpouse, person) =>
    hasSpouse && validatePersonChangedNameDocIssuer({ person })
);

export const validateSpouseAddressOMK = createSelector(
  getApplicantMarriedOrLivingTogether,
  isSpouseAddressMatchWithApplicant,
  isSpouseRegisteredInMoscow,
  getSpouseAddress,
  (hasSpouse, matchWithApplicant, isRegisteredInMoscow, address) =>
    hasSpouse &&
    !matchWithApplicant &&
    isRegisteredInMoscow &&
    validateAddressOMK(address)
);

export const validateSpouseAddressArea = createSelector(
  getApplicantMarriedOrLivingTogether,
  isSpouseAddressMatchWithApplicant,
  isSpouseRegisteredInMoscow,
  getSpouseAddress,
  (hasSpouse, matchWithApplicant, isRegisteredInMoscow, address) =>
    hasSpouse &&
    !matchWithApplicant &&
    isRegisteredInMoscow &&
    validateAddressArea({ address })
);

export const validateSpouseAddressFlat = createSelector(
  getApplicantMarriedOrLivingTogether,
  isSpouseAddressMatchWithApplicant,
  isSpouseRegisteredInMoscow,
  getSpouseAddress,
  (hasSpouse, matchWithApplicant, isRegisteredInMoscow, address) =>
    hasSpouse &&
    !matchWithApplicant &&
    isRegisteredInMoscow &&
    validateAddressFlat({ address })
);

export const validateSpouseAddressDistrict = createSelector(
  getApplicantMarriedOrLivingTogether,
  isSpouseAddressMatchWithApplicant,
  isSpouseRegisteredInMoscow,
  getSpouseAddress,
  (hasSpouse, matchWithApplicant, isRegisteredInMoscow, address) =>
    hasSpouse &&
    !matchWithApplicant &&
    isRegisteredInMoscow &&
    validateAddressDistrict({ address })
);

export const validateSpouseAddressStreet = createSelector(
  getApplicantMarriedOrLivingTogether,
  isSpouseAddressMatchWithApplicant,
  isSpouseRegisteredInMoscow,
  getSpouseAddress,
  (hasSpouse, matchWithApplicant, isRegisteredInMoscow, address) =>
    hasSpouse &&
    !matchWithApplicant &&
    isRegisteredInMoscow &&
    validateAddressStreet({ address })
);

export const validateSpouseAddressHouseAndBuilding = createSelector(
  getApplicantMarriedOrLivingTogether,
  isSpouseAddressMatchWithApplicant,
  isSpouseRegisteredInMoscow,
  getSpouseAddress,
  (hasSpouse, matchWithApplicant, isRegisteredInMoscow, address) =>
    hasSpouse &&
    !matchWithApplicant &&
    isRegisteredInMoscow &&
    validateAddressHouseAndBuilding({ address })
);

export const validateSpouseAddressAddress = createSelector(
  getApplicantMarriedOrLivingTogether,
  isSpouseAddressMatchWithApplicant,
  isSpouseRegisteredInMoscow,
  getSpouseAddress,
  (hasSpouse, matchWithApplicant, isRegisteredInMoscow, address) =>
    hasSpouse &&
    !matchWithApplicant &&
    isRegisteredInMoscow &&
    validateAddressAddress({ address })
);

export const validateSpouseBirthDate = createSelector(
  getApplicantMarriedOrLivingTogether,
  getSpouseBirthDate,
  getSpouseDocumentIssueDate,
  validateDateOfMarriageWithSpousesDates,
  validateSpouseDocumentIssuedDate,
  (hasSpouse, birthdate, dulIssueDate, marriageError, dulIssuedDateError) =>
    hasSpouse &&
    (isDateInAcceptableRange(birthdate) ||
      marriageError ||
      (!!dulIssueDate && dulIssuedDateError))
);
